import { getDefaultState, ILpSaveManager } from '@gametailors/leerplus';
import { GeneratedGameSave, GeneratedHomeworkWeek } from '@repo/backend-api-spec';
import { GameSaveApiService } from '../../services';
import { LpRidMechanicConfig } from '../../typings/schema';
import { IRidCodeLeerplusSaveState } from './rid-game.utils';

type NonHomeworkState = Omit<IRidCodeLeerplusSaveState, 'homework'>;

function getEmptySaveState(): IRidCodeLeerplusSaveState {
  return {
    ...getDefaultState(),
    mechanics: {
      leeslijst_stopwatch: {
        bestTime: null,
        bestAccuracy: null,
      },
    },
    homeworkLinks: { stops: [] },
    journey: { provinceUid: null, locationUid: null },
    homework: { weekIndex: 0, stops: [] },
    plakboekje: [],
    date: null,
  };
}

function stripHomework(state: IRidCodeLeerplusSaveState): NonHomeworkState {
  return {
    missionProgress: state.missionProgress,
    activityProgress: state.activityProgress,
    variables: state.variables,
    inventory: state.inventory,
    mechanics: state.mechanics,
    homeworkLinks: state.homeworkLinks,
    journey: state.journey,
    plakboekje: state.plakboekje,
    date: state.date,
  };
}

function toHomeworkState(homework: GeneratedHomeworkWeek | undefined): IRidCodeLeerplusSaveState['homework'] {
  if (!homework) return { weekIndex: 0, stops: [] };

  return {
    weekIndex: homework.index - 1, // backend's `index` is 1-based; stored 0-based, displayed as `weekIndex + 1`
    stops: homework.stops.map((stop) => ({
      id: stop.uid,
      assignments: stop.assignments.map((assignment) => ({
        uid: assignment.uid,
        // Stored/returned as an opaque JSON blob by the backend (see AssignmentModels.kt) - it isn't typed generically there, but is always shaped like LpRidMechanicConfig by construction.
        config: assignment.config as LpRidMechanicConfig,
      })),
    })),
  };
}

export class GameSaveManager implements ILpSaveManager<IRidCodeLeerplusSaveState> {
  /**
   * The backend answers with an empty save for a player who has none yet, so a failed load says nothing about what's
   * stored: saving after one would replace the player's real save with a fresh one.
   */
  private loaded = false;

  constructor(
    private readonly api: GameSaveApiService,
    private readonly userUid: string,
  ) {}

  public getDefault(): IRidCodeLeerplusSaveState {
    return getEmptySaveState();
  }

  public async load(): Promise<IRidCodeLeerplusSaveState> {
    const response = await this.api.getGameSave(this.userUid);
    if (response.isErr()) {
      console.error('Failed to load game save, playing on a fresh save that is not saved', response.unwrapErr());
      return this.getDefault();
    }

    const save = response.unwrap();
    this.loaded = true;

    return {
      ...this.deserialize(save),
      homework: toHomeworkState(save.homework),
    };
  }

  public async save(save: IRidCodeLeerplusSaveState): Promise<void> {
    if (!this.loaded) return;

    const state = stripHomework(save); // Remove homework before saving, as it's server-derived
    const preferences: Record<string, string> = {};
    for (const [key, value] of Object.entries(state)) {
      preferences[key] = JSON.stringify(value);
    }

    const response = await this.api.upsertGameSave(this.userUid, {
      lastActive: new Date().toISOString(),
      preferences,
      progressElements: [],
    });

    if (response.isErr()) {
      console.error('Failed to save game save', response.unwrapErr());
    }
  }

  public async onPlaythroughComplete(): Promise<void> {
    // noop
  }

  private deserialize(save: GeneratedGameSave): NonHomeworkState {
    const state = stripHomework(this.getDefault());

    for (const [key, raw] of Object.entries(save.preferences)) {
      if (!(key in state)) continue;

      try {
        (state as Record<string, unknown>)[key] = JSON.parse(raw);
      } catch (e) {
        console.error(`Failed to parse stored game save preference "${key}", falling back to default`, e);
      }
    }

    return state;
  }
}

/** The save offline play (`?offline=1`) starts from, with the given homework instead of the backend's. */
export function getOfflineDefaultSave(homework: IRidCodeLeerplusSaveState['homework']): IRidCodeLeerplusSaveState {
  return { ...getEmptySaveState(), homework };
}
